import { cn } from '@lib/utils';
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';


export type CardVariant = 'default' | 'hover' | 'feature' | 'glass';
export type CardPadding = 'none' | 'sm' | 'md' | 'lg';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  padding?: CardPadding;
  gradientClass?: string;
  as?: 'div' | 'article' | 'section';
  children?: ReactNode;
}

const PAD: Record<CardPadding, string> = {
  none: '',
  sm: 'p-4',
  md: 'p-6', // 24 — spec card padding
  lg: 'p-8',
};

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  {
    variant = 'default',
    padding = 'md',
    gradientClass,
    as: Tag = 'div',
    className,
    children,
    ...rest
  },
  ref,
) {
  const base =
    'relative overflow-hidden rounded-2xl transition-all duration-200 ease-apple';

  const byVariant: Record<CardVariant, string> = {
    default: 'bg-white shadow-card',
    hover: 'bg-white shadow-card hover:shadow-card-hover hover:-translate-y-px cursor-default',
    feature: cn(
      'shadow-card',
      gradientClass ?? 'bg-gradient-morning',
    ),
    glass: 'glass border border-white/70 shadow-card',
  };

  return (
    <Tag
      ref={ref as never}
      className={cn(base, byVariant[variant], PAD[padding], className)}
      {...rest}
    >
      {variant === 'feature' ? (
        <>
          <span
            aria-hidden
            className="pointer-events-none absolute -top-8 -right-8 h-32 w-32 rounded-full bg-white/50 blur-3xl"
          />
          <span
            aria-hidden
            className="pointer-events-none absolute -bottom-10 -left-6 h-28 w-28 rounded-full bg-white/40 blur-3xl"
          />
        </>
      ) : null}
      <div className="relative">{children}</div>
    </Tag>
  );
});
